import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { HubOpportunityColumn } from "./hub-opportunity-columns.js";

export interface OpportunityHubColumnsPickerProps {
  columns: HubOpportunityColumn[];
  hiddenColumnIds: string[];
  onHiddenColumnIdsChange: (ids: string[]) => void;
  columnOrder: string[];
  onColumnOrderChange: (order: string[]) => void;
  onReset: () => void;
}

function SortableColumnItem({
  id,
  label,
  isVisible,
  onToggleVisibility,
}: {
  id: string;
  label: string;
  isVisible: boolean;
  onToggleVisibility: () => void;
}) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm",
        isDragging && "z-50 bg-muted shadow-md",
        !isVisible && "opacity-50",
      )}
    >
      <button
        type="button"
        className="shrink-0 cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground active:cursor-grabbing"
        aria-label={t("personaOpportunities.hub.columns.reorderAria", {
          label,
          defaultValue: `Reorder ${label}`,
        })}
        {...attributes}
        {...listeners}
      >
        <Icon name="grip-vertical" size="xs" />
      </button>
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <button
        type="button"
        className="shrink-0 text-muted-foreground/50 hover:text-foreground"
        onClick={onToggleVisibility}
        aria-label={
          isVisible
            ? t("personaOpportunities.hub.columns.hideAria", {
                label,
                defaultValue: `Hide ${label}`,
              })
            : t("personaOpportunities.hub.columns.showAria", {
                label,
                defaultValue: `Show ${label}`,
              })
        }
      >
        <Icon name={isVisible ? "eye" : "eye-off"} size="xs" />
      </button>
    </div>
  );
}

/**
 * Copy of the legacy opportunities column editor: drag to reorder, eye to hide.
 * Renders inline, so the host (the table overflow menu) owns the popover.
 */
export function OpportunityHubColumnsPicker({
  columns,
  hiddenColumnIds,
  onHiddenColumnIdsChange,
  columnOrder,
  onColumnOrderChange,
  onReset,
}: OpportunityHubColumnsPickerProps) {
  const { t } = useTranslation();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const orderedIds = columns.map((column) => column.id);
  const labelById = new Map(columns.map((column) => [column.id, column.header]));
  const isDirty = hiddenColumnIds.length > 0 || columnOrder.length > 0;

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = orderedIds.indexOf(active.id as string);
    const to = orderedIds.indexOf(over.id as string);
    onColumnOrderChange(arrayMove(orderedIds, from, to));
  }

  return (
    <div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
          <div className="max-h-64 overflow-y-auto">
            {orderedIds.map((id) => {
              const isVisible = !hiddenColumnIds.includes(id);
              return (
                <SortableColumnItem
                  key={id}
                  id={id}
                  label={labelById.get(id) ?? id}
                  isVisible={isVisible}
                  onToggleVisibility={() =>
                    onHiddenColumnIdsChange(
                      isVisible
                        ? [...hiddenColumnIds, id]
                        : hiddenColumnIds.filter((hidden) => hidden !== id),
                    )
                  }
                />
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      {isDirty ? (
        <div className="mt-1 border-t border-border pt-1">
          <button
            type="button"
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onReset}
          >
            <Icon name="refresh-cw" size="xs" color="inherit" />
            {t("opportunities.table.resetColumns", "Reset columns")}
          </button>
        </div>
      ) : null}
    </div>
  );
}
